import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Avatar,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';

export default function EditProfile() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    gender: '',
    date_of_birth: '',
    education_level: '',
    profession: '',
    employment_status: '',
    institution: '',
    region: '',
    constituency: '',
    national_id_type: '',
    national_id_number: '',
    hall_of_residence: '',
    student_id_number: '',
    program_of_study: '',
    level: '',
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }

    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('memberToken');
      api.defaults.headers.common['x-auth-token'] = token;

      const response = await api.get('/member-portal/profile');
      const memberData = response.data.data;
      setMember(memberData);
      
      // Populate form with existing data
      setFormData({
        first_name: memberData.first_name || '',
        last_name: memberData.last_name || '',
        phone: memberData.phone || '',
        gender: memberData.gender || '',
        date_of_birth: memberData.date_of_birth || '',
        education_level: memberData.education_level || '',
        profession: memberData.profession || '',
        employment_status: memberData.employment_status || '',
        institution: memberData.institution || '',
        region: memberData.region || '',
        constituency: memberData.constituency || '',
        national_id_type: memberData.national_id_type || '',
        national_id_number: memberData.national_id_number || '',
        hall_of_residence: memberData.hall_of_residence || '',
        student_id_number: memberData.student_id_number || '',
        program_of_study: memberData.program_of_study || '',
        level: memberData.level || '',
      });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
        navigate('/member-portal/login');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setProfilePicture(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePictureUpload = async () => {
    if (!profilePicture) {
      setError('Please select an image file');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');
      
      const formData = new FormData();
      formData.append('profile_picture', profilePicture);

      const response = await api.post('/member-portal/profile/picture', formData);

      setSuccess(response.data.message || 'Profile picture uploaded successfully');
      setProfilePicture(null);
      
      // Refresh member data
      await fetchProfile();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const token = localStorage.getItem('memberToken');
      api.defaults.headers.common['x-auth-token'] = token;

      const response = await api.put('/member-portal/profile', formData);
      setSuccess(response.data.message || 'Profile updated successfully');
      
      // Refresh member data
      await fetchProfile();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
        navigate('/member-portal/login');
      } else {
        setError(err.response?.data?.message || 'Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Container maxWidth="md" sx={{ mb: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/member-portal/profile')}
          >
            Back to Profile
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Edit Profile
          </Typography>

          {/* Profile Picture Upload */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom>
              Profile Picture
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {profilePicture ? (
                <Avatar
                  src={profilePicturePreview}
                  sx={{
                    width: 120,
                    height: 120,
                    bgcolor: 'primary.main',
                    fontSize: '3rem',
                  }}
                >
                  {member?.first_name?.[0] || 'M'}
                </Avatar>
              ) : (
                <MemberProfileAvatar member={member} size={120} sx={{ fontSize: '3rem' }} />
              )}
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="profile-picture-upload"
                  type="file"
                  onChange={handlePictureChange}
                />
                <label htmlFor="profile-picture-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                  >
                    {profilePicture ? 'Change Picture' : 'Upload Picture'}
                  </Button>
                </label>
                {profilePicture && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePictureUpload}
                    disabled={uploading}
                    sx={{ ml: 2 }}
                  >
                    {uploading ? <CircularProgress size={20} /> : 'Save Picture'}
                  </Button>
                )}
              </Box>
              {profilePicture && (
                <Typography variant="caption" color="text.secondary">
                  Selected: {profilePicture.name}
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Profile Form */}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Education Level"
                  name="education_level"
                  value={formData.education_level}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Profession"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Employment Status"
                  name="employment_status"
                  value={formData.employment_status}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Institution"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Region"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Constituency"
                  name="constituency"
                  value={formData.constituency}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="National ID Type"
                  name="national_id_type"
                  value={formData.national_id_type}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="National ID Number"
                  name="national_id_number"
                  value={formData.national_id_number}
                  onChange={handleChange}
                />
              </Grid>

              {/* Student Fields */}
              {member?.is_student && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Hall of Residence"
                      name="hall_of_residence"
                      value={formData.hall_of_residence}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Student ID Number"
                      name="student_id_number"
                      value={formData.student_id_number}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Program of Study"
                      name="program_of_study"
                      value={formData.program_of_study}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Level"
                      name="level"
                      value={formData.level}
                      onChange={handleChange}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/member-portal/profile')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
